import useIdleTimer from "@/components/DirectorySubmissions/Form/hooks/useIdleTimer";
import { useAuth } from "@/contexts/AuthHook";
import { useUserProfiles } from "@/util/hooks";
import { userDisplayName } from "@/util/strings";
import {
  type RealtimeChannel,
  type SupabaseClient,
} from "@supabase/supabase-js";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseFieldLockingParams {
  supabase: SupabaseClient | null;
  submissionId: string | undefined;
  isLoadedSuccessfully: boolean;
}

export function useFieldLocking({
  supabase,
  submissionId,
  isLoadedSuccessfully,
}: UseFieldLockingParams) {
  const { userId } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [collabChannel, setCollabChannel] = useState<RealtimeChannel | null>(
    null,
  );
  const [lockedFields, setLockedFields] = useState<Record<string, string>>({});
  const [lockedQuestions, setLockedQuestions] = useState<
    Record<string, string>
  >({});
  const [presentUserIds, setPresentUserIds] = useState<string[]>([]);
  const [unlockRequest, setUnlockRequest] = useState<{
    fieldId: string;
    requestingUserDisplayName: string;
  } | null>(null);
  const [requestingUnlockFor, setRequestingUnlockFor] = useState<{
    fieldId: string;
    lockingUserId: string;
    lockingUserDisplayName: string;
  } | null>(null);
  const [currentlyEditingField, setCurrentlyEditingField] = useState<
    string | null
  >(null);

  const subscribeRef = useRef<(() => void) | null>(null);

  const { data: userProfilesMap = {} } = useUserProfiles(presentUserIds);

  useEffect(() => {
    if (isLoadedSuccessfully && submissionId && supabase && userId) {
      let currentChannel: RealtimeChannel | null = null;

      const subscribe = () => {
        if (currentChannel) {
          void currentChannel.unsubscribe();
        }

        const handlePresenceSync = () => {
          const state = currentChannel!.presenceState();
          const nextLockedFields: Record<string, string> = {};
          const nextLockedQuestions: Record<string, string> = {};
          const allPresentUserIds: string[] = [];

          for (const userPresences of Object.values(state)) {
            if (userPresences.length > 0) {
              const latestPayload: any =
                userPresences[userPresences.length - 1];
              if (latestPayload.user_id) {
                allPresentUserIds.push(latestPayload.user_id);
              }
              if (latestPayload.editing_field && latestPayload.user_id) {
                nextLockedFields[latestPayload.editing_field] =
                  latestPayload.user_id;
              }
              if (latestPayload.editing_question && latestPayload.user_id) {
                nextLockedQuestions[latestPayload.editing_question] =
                  latestPayload.user_id;
              }
            }
          }

          setLockedFields(nextLockedFields);
          setLockedQuestions(nextLockedQuestions);
          setPresentUserIds(allPresentUserIds);
        };

        currentChannel = supabase
          .channel(`collab-submission-${submissionId}`, {
            config: { presence: { key: userId } },
          })
          .on("presence", { event: "sync" }, handlePresenceSync)
          .on("presence", { event: "join" }, handlePresenceSync)
          .on("presence", { event: "leave" }, handlePresenceSync)
          .on("broadcast", { event: "request_unlock" }, ({ payload }) => {
            if (payload.targetUserId === userId) {
              setUnlockRequest({
                fieldId: payload.fieldId,
                requestingUserDisplayName: payload.requestingUserDisplayName,
              });
            }
          })
          .subscribe((status) => {
            if (status === "SUBSCRIBED") {
              void currentChannel!.track({
                editing_field: null,
                editing_question: null,
                user_id: userId,
              });
            }
          });
        setCollabChannel(currentChannel);
      };
      subscribeRef.current = subscribe;
      subscribe();

      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          subscribeRef.current?.();
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        if (currentChannel) {
          void currentChannel.unsubscribe();
        }
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    }
  }, [isLoadedSuccessfully, submissionId, supabase, userId]);

  const handleFieldFocus = useCallback(
    (fieldId: string, questionId?: string) => {
      const lockingUserId = lockedFields[fieldId];
      if (lockingUserId && lockingUserId !== userId) {
        const profile = userProfilesMap[lockingUserId];
        const displayName = profile ? userDisplayName(profile) : "Someone";
        enqueueSnackbar(`${displayName} is currently editing this field`, {
          variant: "warning",
        });
        return false;
      }

      setCurrentlyEditingField(fieldId);
      const isReady = collabChannel?.state === "joined";
      if (isReady) {
        void collabChannel?.track({
          editing_field: fieldId,
          user_id: userId,
          editing_question: questionId,
        });
      } else {
        console.log("Channel not ready, resubscribing...");
        subscribeRef.current?.(); // Call resubscribe function
      }
      return true;
    },
    [lockedFields, collabChannel, userId, userProfilesMap],
  );

  const handleFieldBlur = useCallback(
    (fieldId: string) => {
      // If a modal is active asking to unlock a field, don't process the blur.
      // The modal's actions will handle the unlock logic.
      if (unlockRequest?.fieldId === fieldId) {
        return;
      }

      handleUnlockField(fieldId);
    },
    [collabChannel, userId, unlockRequest],
  );

  const handleUnlockField = useCallback(
    (fieldId: string) => {
      // update presence
      void collabChannel?.track({
        editing_field: null,
        editing_question: null,
        user_id: userId,
      });

      // blur the element
      const element = document.querySelector(
        `[data-field-id="${fieldId}"][contenteditable="true"]`,
      ) as HTMLElement;
      element?.blur();

      setCurrentlyEditingField(null);
    },
    [collabChannel, userId],
  );

  const sendFieldUnlockRequest = useCallback(
    (fieldId: string, targetUserId: string) => {
      if (collabChannel?.state === "joined") {
        void collabChannel.send({
          type: "broadcast",
          event: "request_unlock",
          payload: {
            fieldId,
            targetUserId,
            requestingUserId: userId,
            requestingUserDisplayName: userId
              ? userDisplayName(userProfilesMap[userId])
              : "Someone",
          },
        });
      }
    },
    [collabChannel, userId, userProfilesMap],
  );

  const requestFieldUnlock = useCallback(
    (data: {
      fieldId: string;
      lockingUserId: string;
      lockingUserDisplayName: string;
    }) => {
      setRequestingUnlockFor(data);
    },
    [],
  );

  const handleAcceptUnlock = () => {
    if (unlockRequest) {
      handleUnlockField(unlockRequest.fieldId);
      setUnlockRequest(null);
    }
  };

  const handleRejectUnlock = () => {
    if (unlockRequest) {
      const fieldIdToRefocus = unlockRequest.fieldId;

      setUnlockRequest(null);

      // Use setTimeout to run this after the modal has closed and the UI has updated.
      setTimeout(() => {
        const element = document.querySelector(
          `[data-field-id="${fieldIdToRefocus}"][contenteditable="true"]`,
        ) as HTMLElement;

        // focus and move cursor to end of content
        if (element) {
          element.focus();
          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.selectNodeContents(element);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }, 0);
    }
  };

  const { resetIdleTimer } = useIdleTimer(
    handleUnlockField,
    currentlyEditingField,
  );

  // Reset timer when field changes
  useEffect(() => {
    if (currentlyEditingField) {
      resetIdleTimer();
    }
  }, [currentlyEditingField, resetIdleTimer]);

  return {
    currentlyEditingField,
    lockedFields,
    lockedQuestions,
    unlockRequest,
    handleFieldFocus,
    handleFieldBlur,
    sendFieldUnlockRequest,
    handleAcceptUnlock,
    handleRejectUnlock,
    requestFieldUnlock,
    requestingUnlockFor,
    setRequestingUnlockFor,
    userProfiles: userProfilesMap,
  };
}
